import React, { useState, useEffect } from 'react';
import { Trash2, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import type { LineItem } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const LineItemsTab: React.FC = () => {
  const { user } = useAuth();
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingLines, setDeletingLines] = useState(false);
  const [showAddLineForm, setShowAddLineForm] = useState(false);
  const [newLine, setNewLine] = useState('');
  const [addingLine, setAddingLine] = useState(false);

  useEffect(() => {
    fetchLineItems();
  }, []);

  const fetchLineItems = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('line_items')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLineItems(data || []);
    } catch (error) {
      console.error('Error fetching line items:', error);
      toast.error('Failed to fetch line items');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllLines = async () => {
    if (!confirm('Are you sure you want to delete all line items? This action cannot be undone.')) {
      return;
    }

    setDeletingLines(true);
    try {
      const { data: items, error: fetchError } = await supabase
        .from('line_items')
        .select('id');

      if (fetchError) throw fetchError;

      if (!items || items.length === 0) {
        toast.info('No line items to delete');
        return;
      }

      const { error: deleteError } = await supabase
        .from('line_items')
        .delete()
        .in('id', items.map(item => item.id));

      if (deleteError) throw deleteError;

      await supabase.from('system_logs').insert({
        user_id: user?.id,
        action: 'DELETE_ALL_LINES',
        details: `Deleted ${items.length} line items`
      });

      toast.success(`Successfully deleted ${items.length} line items`);
      setLineItems([]);
    } catch (error) {
      console.error('Error deleting line items:', error);
      toast.error('Failed to delete line items');
    } finally {
      setDeletingLines(false);
    }
  };

  const handleAddLine = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLine.trim()) {
      toast.error('Please enter at least one line');
      return;
    }

    const lines = newLine.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      toast.error('Please enter at least one valid line');
      return;
    }

    setAddingLine(true);
    try {
      const lineItems = lines.map(line => ({
        content: line.trim()
      }));

      const { error } = await supabase
        .from('line_items')
        .insert(lineItems);

      if (error) throw error;

      await supabase.from('system_logs').insert({
        user_id: user?.id,
        action: 'ADD_LINES',
        details: `Added ${lines.length} line items`
      });

      toast.success(`Successfully added ${lines.length} lines`);
      setNewLine('');
      setShowAddLineForm(false);
      fetchLineItems();
    } catch (error) {
      console.error('Error adding lines:', error);
      toast.error('Failed to add lines');
    } finally {
      setAddingLine(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Line Items</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowAddLineForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Lines
          </button>
          <button
            onClick={handleDeleteAllLines}
            disabled={deletingLines || lineItems.length === 0}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
              deletingLines || lineItems.length === 0
                ? 'bg-red-400 dark:bg-red-500 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600'
            }`}
          >
            {deletingLines ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete All Lines
              </>
            )}
          </button>
        </div>
      </div>

      {showAddLineForm && (
        <div className="mb-6 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
          <form onSubmit={handleAddLine} className="space-y-4">
            <div>
              <label htmlFor="newLine" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Line Content (One line per row)
              </label>
              <textarea
                id="newLine"
                rows={10}
                value={newLine}
                onChange={(e) => setNewLine(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-600 dark:border-gray-600 dark:text-white"
                placeholder="Enter lines here, one per row..."
              />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                You can paste multiple lines at once. Each line will be added as a separate item.
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={addingLine}
                className={`flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                  addingLine ? 'bg-indigo-400 dark:bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600'
                }`}
              >
                {addingLine ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Adding...
                  </>
                ) : (
                  'Add Lines'
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowAddLineForm(false)}
                className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 dark:border-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Content</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Created At</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {lineItems.map((item) => (
              <tr key={item.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{item.content}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{new Date(item.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LineItemsTab;